import { useMemo } from 'react';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { getDimensionData } from '@api/dimensionContainer';
import instance from '@api/axiosInstance';
import { trackQueryError } from '@utils/sensor/apptracker';
import { DATASET } from '@utils/constants';
import { useSelector } from 'react-redux';
import { intervalSelector } from '@src/store/common/selectors';

/**
 * Hook to fetch top 200 events for breadcrumb dropdown
 * Similar to useEventsOverview but limited to 200 events
 */
function useTopEvents() {
  const interval = useSelector(intervalSelector);
  const EVENT_DIMENSION = 'event_name';
  const metric = 'AppTotalEvents';

  // Build group-by query to fetch top 200 events
  const groupByQuery = useMemo(() => {
    if (!interval || !interval.from || !interval.to) {
      return null;
    }

    return {
      type: 'group-by',
      dataset: DATASET,
      metrics: [metric],
      interval: `${moment(interval.from).toISOString()}/${moment(interval.to).toISOString()}`,
      granularity: 'ALL',
      filter: [],
      limit: 200,
      groupBy: [EVENT_DIMENSION],
      sortBy: [{ metric, order: 'desc' }],
      options: {
        withTotals: true,
      },
    };
  }, [metric, interval]);

  // Fetch events data using react-query
  const {
    data = { data: [], totals: {} },
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['breadcrumb-events', groupByQuery],
    queryFn: () => getDimensionData({ queries: [groupByQuery] }, {}, instance),
    enabled: !!groupByQuery,
    refetchOnWindowFocus: false,
    select: (response) => {
      const { data = [], totals = {} } = get(response, ['data', 0], {});
      return { data, totals };
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (err) => {
      trackQueryError({ type: 'breadcrumb_top_events', err });
    },
  });

  // Process event list from API response
  const eventList = useMemo(() => {
    const eventRows = get(data, ['data', '0', 'results'], []);
    if (eventRows.length > 0) {
      const validEvents = eventRows.filter(
        (eachEvent) =>
          !['UNKNOWN'].includes(eachEvent[EVENT_DIMENSION]) && !isNaN(eachEvent[metric]),
      );
      return validEvents.map((event) => event[EVENT_DIMENSION]);
    }
    return [];
  }, [data, metric]);

  return {
    eventList,
    isLoading: isFetching,
    isError,
  };
}

export default useTopEvents;
